import { useMemo } from 'react';
import { UMSidePanel } from './UMSidePanel';
import {
  UMButton,
  UMAccordionSection,
  UMCheckboxRow,
  UMFormGroup,
  UMFormStyles,
  UMInput,
  UMMinMax,
  UMMultiSelect,
  UMRadioGroup,
  UMRangeSlider,
  UMSegmentedControl,
  type UMRange,
} from './UMForm';

/**
 * "Filtres des bâtiments" panel — exact production catalogue (sections, fields, options, order).
 * Catalogue-driven: prototypes can add/remove fields by editing a copy of UM_FILTER_CATALOG.
 * Spec: docs/urbanmap/reference/panels.md
 */

export type UMFilterField =
  | { kind: 'perimeter'; id: string; placeholder: string }
  | { kind: 'multiselect'; id: string; label: string; options: string[] }
  | { kind: 'range'; id: string; label: string }
  | { kind: 'radio'; id: string; label: string; options: string[] }
  | { kind: 'text'; id: string; label: string }
  | { kind: 'checkboxes'; id: string; label?: string; options: string[] }
  | { kind: 'segmented'; id: string; options: string[] }
  | { kind: 'distance'; id: string; label: string; max: number; step: number };

export interface UMFilterSection {
  id: string;
  title: string;
  defaultOpen?: boolean;
  fields: UMFilterField[];
}

const ENERGY_HEAT = ['Réseau', 'Gaz', 'Electricité', 'Fioul', 'GPL', 'Biomasse', 'Autre', 'Charbon', 'Inconnu'];
const YES_NO = ['Indifférent', 'Oui', 'Non'];

export const UM_BUILDING_USAGES = [
  'Habitat individuel', 'Habitat collectif', 'Bureaux', 'Restaurant/café', 'Hôtel', 'Centre commercial', 'Autres commerces',
  'Enseignement', 'Groupes scolaires', 'Enseignement supérieur', 'Centre sportif (gymnase, palais omnisports)', 'Piscine',
  'Cinema, théâtre, salles de spectacles', 'Musée', 'Santé', 'Clinique / Hôpital', 'Maison médicalisée / maison de retraite / EPHAD',
  'Gare', 'Aérogare', 'Industriel et agricole', "Stock de matière / Silo / réservoir / château d'eau", 'Culte', 'Non consommateur',
];

export const UM_FILTER_CATALOG: UMFilterSection[] = [
  { id: 'perimeter', title: 'Périmètre', defaultOpen: true, fields: [{ kind: 'perimeter', id: 'perimeter', placeholder: 'Rechercher une ville / département' }] },
  {
    id: 'characteristics',
    title: 'Caractéristiques des bâtiments',
    defaultOpen: true,
    fields: [
      { kind: 'multiselect', id: 'usages', label: 'Usages principaux', options: UM_BUILDING_USAGES },
      { kind: 'range', id: 'dwellings', label: 'Nombre de logements' },
      { kind: 'range', id: 'year', label: 'Année de construction' },
      { kind: 'range', id: 'area', label: 'Superficie' },
    ],
  },
  {
    id: 'heat',
    title: 'Énergie & consommations chaud',
    defaultOpen: true,
    fields: [
      { kind: 'range', id: 'heatConsumption', label: 'Consommation retenue (MWhu)' },
      { kind: 'multiselect', id: 'heatingEnergy', label: 'Énergie de chauffage', options: ENERGY_HEAT },
      { kind: 'multiselect', id: 'hotWaterEnergy', label: "Énergie d'ECS", options: ENERGY_HEAT.filter((e) => e !== 'GPL') },
      {
        kind: 'multiselect', id: 'heatingType', label: 'Type de chauffage et ECS',
        options: ['Chauffage - Individuel', 'Chauffage - Collectif', 'Chauffage - Inconnu', 'Eau chaude - Individuel', 'Eau chaude - Collectif', 'Eau chaude - Inconnu'],
      },
    ],
  },
  {
    id: 'cold',
    title: 'Énergie & consommations froid',
    defaultOpen: true,
    fields: [
      { kind: 'range', id: 'coldConsumption', label: 'Consommation retenue (MWhu)' },
      { kind: 'multiselect', id: 'coolingEnergy', label: 'Énergie de climatisation', options: ['Réseau', 'Electricité', 'Autre', 'Inconnu'] },
      { kind: 'multiselect', id: 'coolingType', label: 'Type de climatisation', options: ['Individuel', 'Collectif', 'Inconnu'] },
      { kind: 'multiselect', id: 'coolingInstallation', label: "Type d'installation de clim", options: ['Refroidissement à sec', 'Tour de refroidissement', 'Système split', 'Aucun', 'Inconnu'] },
      { kind: 'range', id: 'fans', label: 'Nombre de ventilateurs' },
      { kind: 'radio', id: 'icpe2921', label: 'Présence tour aero ICPE (2921)', options: YES_NO },
    ],
  },
  {
    id: 'owner',
    title: 'Propriétaire & Syndic',
    fields: [
      { kind: 'text', id: 'owner', label: 'Propriétaire' },
      { kind: 'checkboxes', id: 'ownerType', label: 'Type de propriétaire', options: ['Public', 'Privé', 'Inconnu'] },
      { kind: 'text', id: 'syndic', label: 'Nom du syndic' },
    ],
  },
  {
    id: 'distance',
    title: 'Distance aux réseaux',
    fields: [
      { kind: 'segmented', id: 'distanceMode', options: ['Par type', 'Par nom'] },
      { kind: 'checkboxes', id: 'networkType', options: ['Chaud', 'Froid'] },
      { kind: 'distance', id: 'distance', label: 'Distance (m)', max: 510, step: 10 },
    ],
  },
  {
    id: 'sources',
    title: 'Sources de consommations',
    fields: [
      { kind: 'radio', id: 'hasEngie', label: 'Données ENGIE existantes', options: YES_NO },
      { kind: 'range', id: 'engieConsumption', label: 'Consommation ENGIE (MWhu)' },
      { kind: 'range', id: 'engiePdl', label: 'Nombre de PDL ENGIE' },
      { kind: 'radio', id: 'hasGrdf', label: 'Données DLE GRDF existantes', options: YES_NO },
      { kind: 'range', id: 'grdfConsumption', label: 'Consommation DLE GRDF (MWhu)' },
      { kind: 'range', id: 'grdfPdl', label: 'Nombre de PDL GRDF' },
      { kind: 'radio', id: 'hasEnedis', label: 'Données DLE ENEDIS existantes', options: YES_NO },
      { kind: 'range', id: 'enedisConsumption', label: 'Consommation DLE ENEDIS (MWhu)' },
      { kind: 'range', id: 'enedisPdl', label: 'Nombre de PDL ENEDIS' },
      { kind: 'radio', id: 'hasDleNetwork', label: 'Données DLE Réseaux existantes', options: YES_NO },
      { kind: 'range', id: 'dleHeat', label: 'Conso. chaud DLE Réseaux (MWhu)' },
      { kind: 'range', id: 'dleCold', label: 'Conso. froid DLE Réseaux (MWhu)' },
    ],
  },
];

/** Filter values keyed by field id: string (text/perimeter/radio/segmented), string[] (multiselect/checkboxes), UMRange, [number, number] (distance). */
export type UMFilterValues = Record<string, string | string[] | UMRange | [number, number] | undefined>;

/** True when a field differs from its production default (drives the "Réinitialiser" button). */
export function isFilterActive(field: UMFilterField, value: UMFilterValues[string]): boolean {
  if (value === undefined) return false;
  switch (field.kind) {
    case 'radio':
      return value !== field.options[0];
    case 'segmented':
      return false;
    case 'distance': {
      const [a, b] = value as [number, number];
      return a > 0 || b < field.max;
    }
    case 'range': {
      const r = value as UMRange;
      return Boolean(r.min || r.max);
    }
    default:
      return Array.isArray(value) ? value.length > 0 : Boolean(value);
  }
}

export interface UMFiltersPanelProps {
  onClose: () => void;
  values: UMFilterValues;
  onChange: (values: UMFilterValues) => void;
  catalog?: UMFilterSection[];
}

export function UMFiltersPanel({ onClose, values, onChange, catalog = UM_FILTER_CATALOG }: UMFiltersPanelProps) {
  const set = (id: string, v: UMFilterValues[string]) => onChange({ ...values, [id]: v });
  const active = useMemo(() => catalog.some((s) => s.fields.some((f) => isFilterActive(f, values[f.id]))), [catalog, values]);

  const renderField = (f: UMFilterField) => {
    switch (f.kind) {
      case 'perimeter':
        return <UMInput key={f.id} value={(values[f.id] as string) ?? ''} onChange={(v) => set(f.id, v)} placeholder={f.placeholder} icon="search" />;
      case 'multiselect':
        return (
          <UMFormGroup key={f.id} label={f.label}>
            <UMMultiSelect ariaLabel={f.label} options={f.options} value={(values[f.id] as string[]) ?? []} onChange={(v) => set(f.id, v)} />
          </UMFormGroup>
        );
      case 'range':
        return <UMMinMax key={f.id} label={f.label} value={(values[f.id] as UMRange) ?? { min: '', max: '' }} onChange={(v) => set(f.id, v)} />;
      case 'radio':
        return (
          <UMFormGroup key={f.id} label={f.label}>
            <UMRadioGroup name={f.id} options={f.options} value={(values[f.id] as string) ?? f.options[0]} onChange={(v) => set(f.id, v)} />
          </UMFormGroup>
        );
      case 'text':
        return (
          <UMFormGroup key={f.id} label={f.label}>
            <UMInput ariaLabel={f.label} value={(values[f.id] as string) ?? ''} onChange={(v) => set(f.id, v)} />
          </UMFormGroup>
        );
      case 'checkboxes': {
        const row = <UMCheckboxRow options={f.options} value={(values[f.id] as string[]) ?? []} onChange={(v) => set(f.id, v)} />;
        return f.label ? <UMFormGroup key={f.id} label={f.label}>{row}</UMFormGroup> : <div key={f.id}>{row}</div>;
      }
      case 'segmented':
        return (
          <div key={f.id}>
            <UMSegmentedControl options={f.options} value={(values[f.id] as string) ?? f.options[0]} onChange={(v) => set(f.id, v)} />
          </div>
        );
      case 'distance':
        return (
          <UMFormGroup key={f.id} label={f.label}>
            <UMRangeSlider
              min={0}
              max={f.max}
              step={f.step}
              value={(values[f.id] as [number, number]) ?? [0, f.max]}
              onChange={(v) => set(f.id, v)}
              format={(v) => (v >= f.max - f.step ? `${f.max - f.step}+ m` : `${v} m`)}
            />
          </UMFormGroup>
        );
    }
  };

  return (
    <UMSidePanel
      title="Filtres des bâtiments"
      onClose={onClose}
      footer={<UMButton disabled={!active} onClick={() => onChange({})}>Réinitialiser</UMButton>}
    >
      <UMFormStyles />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {catalog.map((s) => (
          <UMAccordionSection key={s.id} title={s.title} defaultOpen={s.defaultOpen}>
            {s.fields.map(renderField)}
          </UMAccordionSection>
        ))}
      </div>
    </UMSidePanel>
  );
}
